// ============================================================================
// Phase 5 Domain 8 Feature 10: Offering Tag Display Contracts
//
// Shared normalized shape for tag relationships on offering surfaces:
// - Calendar ScheduleCard context rows
// - Session detail TagsCard
// - Event instance detail TagsCard
// ============================================================================

import { z } from 'zod';

// ============================================================================
// Zod Schemas (for use in other Zod-based DTOs like CalendarItem, EventInstanceDetail)
// ============================================================================

export const tagContextLineSchema = z.object({
  tagId: z.string(),
  tagName: z.string(),
  tagColor: z.string(),
});

export const offeringTagDisplaySchema = z.object({
  requiredTags: z.array(tagContextLineSchema),
  awardTags: z.array(tagContextLineSchema),
  ruleMode: z.enum(['ANY', 'ALL']),
  pathOwnership: z.object({
    pathId: z.string(),
    pathName: z.string(),
  }).nullable(),
});

// ============================================================================
// TypeScript Interfaces (inferred from Zod schemas)
// ============================================================================

export type TagContextLine = z.infer<typeof tagContextLineSchema>;
export type OfferingTagDisplay = z.infer<typeof offeringTagDisplaySchema>;

/**
 * Check whether an OfferingTagDisplay has any meaningful tag relationships.
 * Used as a rendering gate — surfaces hide entirely when this returns false.
 */
export function hasTagRelationships(tags: OfferingTagDisplay): boolean {
  return tags.awardTags.length > 0
    || tags.requiredTags.length > 0
    || tags.pathOwnership !== null;
}
